import React from "react";
import { Guid } from "guid-typescript";
import { connect } from "react-redux";
import { IAppState } from "../../state/Store";
import { setOpenTab, TabStatus } from "../../state/system/Tabs";

import TabContainer from "../../components/tabs/TabContainer";

import "./Tabs.scss";

export class TabWillChangeEvent {
    private _tab: string;
    get tab(): string {
        return this._tab;
    }
    private _complete: () => void;
    get complete(): () => void {
        return this._complete;
    }

    cancel: boolean;
    asynComplete: boolean;

    constructor(tab: string, complete: () => void) {
        this._tab = tab;
        this.cancel = false;
        this._complete = complete;
    }
}

interface TabsProps {
    name?: string;
    boxed?: boolean;
    groups?: TabStatus[];
    children?:
        | React.ReactElement<TabContainer>[]
        | React.ReactElement<TabContainer>;
    onTabChange?: (newTab: string) => void;
    onTabWillChange?: (event: TabWillChangeEvent) => void;
    setOpenTab?: (state: TabStatus) => void;
}

interface TabsState {
    name?: string;
}

class Tabs extends React.Component<TabsProps, TabsState> {
    state = {
        name: null,
    };

    static getDerivedStateFromProps(
        props: TabsProps,
        state: TabsState,
    ): TabsState {
        state.name = state.name || props.name || Guid.create().toString();

        return state;
    }

    componentDidMount(): void {
        let hash = window.location.hash.replace("#", "");
        hash = hash ? decodeURIComponent(hash) : hash;

        if (hash && this.getTabs().find(tab => tab.props.header === hash))
            this.setSelectedTab(hash);
        else {
            const persisted = this.props.groups.find(
                group => group.tabGroup === this.state.name,
            );

            if (!persisted)
                this.setSelectedTab(
                    this.getTabs().find(tab => !tab.props.hidden)?.props
                        ?.header,
                );
            else this.props.onTabChange?.(persisted.openTab);
        }
    }

    getTabs() {
        return this.props.children instanceof Array
            ? this.props.children
            : [this.props.children];
    }

    private setSelectedTab(tab: string) {
        const tabs = this.getTabs();
        const tabEntry = tabs.find(t => t.props.header === tab);

        if (!tabEntry || tabEntry.props.hidden)
            tab = tabs.find(t => !t.props.hidden)?.props?.header;

        const event = new TabWillChangeEvent(tab, () =>
            this.completeSelectTab(event),
        );

        this.props.onTabWillChange?.(event);

        if (event.cancel) return;

        if (!event.asynComplete) this.completeSelectTab(event);
    }
    private completeSelectTab(event: TabWillChangeEvent) {
        if (!event.cancel) {
            window.history.pushState(
                null,
                "",
                `${window.location.pathname}#${encodeURIComponent(event.tab)}`,
            );
            this.props.setOpenTab({
                tabGroup: this.state.name,
                openTab: event.tab,
            } as TabStatus);
            this.props.onTabChange?.(event.tab);
        }
    }

    render(): React.ReactNode {
        const tabs = this.getTabs();
        const openTab = this.props.groups.find(
            group => group.tabGroup === this.state.name,
        );

        return (
            <div className="tabs-container">
                <div className="tabs-header">
                    <div className="tab-button-container">
                        {tabs.map((tab, index) => {
                            return !tab.props.hidden ? (
                                <button
                                    key={index}
                                    tabIndex={0}
                                    className={`tab${
                                        openTab?.openTab === tab.props.header
                                            ? " selected"
                                            : ""
                                    }${tab.props.dirty ? " dirty" : ""}`}
                                    onClick={() =>
                                        this.setSelectedTab(tab.props.header)
                                    }>
                                    {tab.props.header}
                                </button>
                            ) : null;
                        })}
                    </div>
                </div>

                {tabs.map((tab, index) => {
                    return !tab.props.hidden &&
                        openTab?.openTab === tab.props.header ? (
                        <div
                            key={index}
                            className={`tab${
                                this.props.boxed ? " boxed" : ""
                            }`}>
                            {tab.props.children}
                        </div>
                    ) : null;
                })}
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { tabs } = state.db;
    return {
        groups: tabs.tabGroups,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setOpenTab: (state: TabStatus) => dispatch(setOpenTab(state)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Tabs);
